import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Search, ChevronDown, MapPin, ShoppingBag, Waves, Store, Navigation, Coffee, Star, List, ArrowLeft, X, DollarSign } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Location } from '../../types';
import { BRANDS, BRAND_ICONS, normalizeBrandName, Brand, LOCATION_ONLY_BRANDS } from '../../utils/brands';
import { calculateDistance, formatDistance } from '../../utils/distance';
import { createPortal } from 'react-dom';
import beachesData from '../../assets/beaches.json';
import { reverseGeocode } from '../../services/geoapify';
import { getShoppingCenterImageUrl } from '../../utils/shoppingCenterImages';
import { 
  preprocessLocation, 
  locationHasType, 
  getTypeLabel,
  getCategoryLabel,
  getPopularTypeIds,
  getCategories,
  getTypesForCategory,
  Category,
  Type
} from '../../utils/coffeeFilterMatcherV4';
import { NormalizedSize } from '../../utils/sizeNormalizer';
import { updateThemeColor } from '../../utils/themeColor';
import AppShell from '../layout/AppShell';
import { HeaderConfig } from '../layout/AppHeader';
import './Home2.css';

interface Home2Props {
  allLocations?: Record<string, Location>;
  allCoffeeLocations?: Record<string, any>; // Coffee locations loaded from App.tsx
  preprocessedCoffeeLocations?: Record<string, any>; // Preprocessed coffee locations (type/category/size normalized) from App.tsx
  userLocation?: [number, number] | null; // App-wide current user location
  onRequestLocation?: () => Promise<[number, number]>; // Trigger browser location prompt (App updates userLocation)
  isLocating?: boolean; // App-wide locating state
  onLocationClick: (location: Location) => void;
  onCoffeeLocationClick?: (location: any) => void; // Handler for coffee location clicks
  onSearchClick?: (referenceLocation: [number, number] | null) => void;
  onBrandClick?: (
    brand: string,
    locations: Array<Location & { distance?: number }>,
    referenceLocation: [number, number] | null
  ) => void;
  onShowAllClick?: (
    type: 'shopping' | 'beaches' | 'coffee',
    locations: Array<Location & { distance?: number }>,
    referenceLocation: [number, number] | null
  ) => void;
  onShowAllBrandsClick?: (referenceLocation: [number, number] | null) => void;
  onLocationChange?: (location: [number, number] | null) => void; // Callback to update map center
  onTabChange?: (tab: string) => void; // Tab change handler for AppShell
  activeTab?: string; // Active tab for AppShell
  onMyVibeClick?: () => void; // My Vibe click handler
  hideNavbar?: boolean; // Hide navbar for native app
}

interface AustralianCity {
  state: string;
  city: string;
  latitude: number;
  longitude: number;
}

// Coffee type image mapping
const COFFEE_TYPE_IMAGES: Record<string, string> = {
  'type_cappuccino_hot': 'https://images.unsplash.com/photo-1534778101976-62847782c213?q=80&w=774&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_macchiato': 'https://images.unsplash.com/photo-1485808191679-5f86510681a2?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_flat_white_hot': 'https://images.unsplash.com/photo-1577968897966-3d4325b36b61?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_iced_coffee': 'https://images.unsplash.com/photo-1578314675249-a6910f80cc4e?q=80&w=1914&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_espresso_shot': 'https://images.unsplash.com/photo-1610889556528-9a770e32642f?q=80&w=1915&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_hot_chocolate_classic': 'https://images.unsplash.com/photo-1542990253-0d0f5be5f0ed?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_chai_latte': 'https://images.unsplash.com/photo-1578899952107-9c390f1af1b7?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_iced_latte': 'https://images.unsplash.com/photo-1517701550927-30cf4ba1dba5?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_tea_any': 'https://images.unsplash.com/photo-1562547256-2c5ee93b60b7?q=80&w=1041&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_latte_hot': 'https://images.unsplash.com/photo-1570968915860-54d5c301fa9f?q=80&w=1035&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_long_black_hot': 'https://images.unsplash.com/photo-1587734361993-0033759da68b?q=80&w=1036&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_piccolo': 'https://cdn.stocksnap.io/img-thumbs/960w/coffee-cafe_GFSJVKYET2.jpg',
  'type_mocha_hot': 'https://images.unsplash.com/photo-1618576230663-9714aecfb99a?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
  'type_matcha_latte': 'https://images.unsplash.com/photo-1582785513054-8d1bf9d69c1a?q=80&w=1035&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
};

const AUSTRALIAN_CITIES: AustralianCity[] = [
  // New South Wales
  { state: 'New South Wales', city: 'Sydney', latitude: -33.8688, longitude: 151.2093 },
  { state: 'New South Wales', city: 'Newcastle', latitude: -32.9283, longitude: 151.7817 },
  { state: 'New South Wales', city: 'Wollongong', latitude: -34.4278, longitude: 150.8931 },
  { state: 'New South Wales', city: 'Byron Bay', latitude: -28.6474, longitude: 153.602 },
  // Victoria
  { state: 'Victoria', city: 'Melbourne', latitude: -37.8136, longitude: 144.9631 },
  // Queensland
  { state: 'Queensland', city: 'Brisbane', latitude: -27.4698, longitude: 153.0251 },
  { state: 'Queensland', city: 'Gold Coast', latitude: -28.0167, longitude: 153.4 },
  // South Australia
  { state: 'South Australia', city: 'Adelaide', latitude: -34.9285, longitude: 138.6007 },
  // Western Australia
  { state: 'Western Australia', city: 'Perth', latitude: -31.9505, longitude: 115.8605 },
  // Australian Capital Territory
  { state: 'Australian Capital Territory', city: 'Canberra', latitude: -35.2809, longitude: 149.13 },
];

const Home2: React.FC<Home2Props> = ({ 
  allLocations = {}, 
  allCoffeeLocations = {},
  preprocessedCoffeeLocations = {},
  userLocation,
  onRequestLocation,
  isLocating = false,
  onLocationClick,
  onCoffeeLocationClick,
  onSearchClick,
  onBrandClick,
  onShowAllClick,
  onShowAllBrandsClick,
  onLocationChange,
  onTabChange,
  activeTab = 'home2',
  onMyVibeClick,
  hideNavbar = false,
}) => {
  // Load persisted location from localStorage (city + useCurrentLocation only; coords come from App userLocation)
  const loadPersistedLocation = (): { city: AustralianCity | null; useCurrentLocation: boolean } => {
    try {
      const stored = localStorage.getItem('vibecheck_home2_location');
      if (stored) {
        const parsed = JSON.parse(stored);
        const city = parsed.cityName 
          ? AUSTRALIAN_CITIES.find((c) => c.city === parsed.cityName) || null
          : null;
        return {
          city,
          useCurrentLocation: parsed.useCurrentLocation || false,
        };
      }
    } catch (e) {
      console.error('Error loading persisted location:', e);
    }
    return {
      city: AUSTRALIAN_CITIES.find((c) => c.city === 'Sydney') || AUSTRALIAN_CITIES[0],
      useCurrentLocation: false,
    };
  };

  const persistedLocation = loadPersistedLocation();
  const [selectedCity, setSelectedCity] = useState<AustralianCity | null>(persistedLocation.city);
  const [useCurrentLocation, setUseCurrentLocation] = useState(persistedLocation.useCurrentLocation);
  const [currentSuburb, setCurrentSuburb] = useState<string>('');
  const [isFetchingSuburb, setIsFetchingSuburb] = useState(false);
  const [showCityFilter, setShowCityFilter] = useState(false);
  const [shoppingCentres, setShoppingCentres] = useState<Array<Location & { distance?: number }>>([]);
  const [beaches, setBeaches] = useState<Array<Location & { distance?: number }>>([]);
  const [coffeeLocations, setCoffeeLocations] = useState<Array<any & { distance?: number; price?: number | null; rating?: number | null }>>([]);
  const [allFilteredCoffeeLocations, setAllFilteredCoffeeLocations] = useState<Array<any & { distance?: number; price?: number | null; rating?: number | null }>>([]);
  const [displayedCoffeeCount, setDisplayedCoffeeCount] = useState(10);
  const coffeeCardsScrollRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Simple debounce ref to prevent rapid double-taps
  const lastClickTimeRef = useRef<number>(0);
  
  // Touch handling refs for "Show all" buttons to distinguish scrolling from tapping
  const showAllTouchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const showAllIsScrollingRef = useRef<boolean>(false);
  const showAllScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Popular cafes list (same as CoffeeMapper2)
  const POPULAR_CAFES = [
    'Skittle Lane',
    'Edition Roasters MidCity',
    'Gumption by Coffee Alchemy',
    'Regiment Cafe',
    'Sevens Specialty Coffee CBD',
    'Leible Coffee Pitt St',
    "Ek'sentrik Cafe Kent Street",
    "Diggy Doo's",
  ];

  // Coffee filter state - load from localStorage
  const loadPersistedCoffeeFilter = (): { 
    cafeFilter: 'popular' | 'all'; 
    typeFilter: 'popular' | 'categories' | null; 
    categoryId: string | null; 
    typeId: string | null; 
    temperature: 'hot' | 'cold' | null;
    coffeeSortBy?: 'cheapest' | 'best-rated' | 'popular';
    coffeeTypeFilter?: string | null;
    coffeeLocationFilter?: 'near-me' | 'city' | 'everywhere';
  } => {
    try {
      const stored = localStorage.getItem('vibecheck_home2_coffee_filter');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate old format to new format
        if (parsed.mode) {
          const cafeFilter = parsed.mode === 'popular' ? 'popular' : 'all';
          const typeFilter = parsed.mode === 'categories' ? 'categories' : 'popular';
          return {
            cafeFilter,
            typeFilter,
            categoryId: parsed.categoryId || null,
            typeId: parsed.typeId || null,
            temperature: parsed.temperature || null,
            coffeeSortBy: parsed.coffeeSortBy || 'popular',
            coffeeTypeFilter: parsed.coffeeTypeFilter || parsed.typeId || 'type_cappuccino_hot',
            coffeeLocationFilter: parsed.coffeeLocationFilter || 'city',
          };
        }
        return {
          cafeFilter: parsed.cafeFilter || 'all',
          typeFilter: parsed.typeFilter || null,
          categoryId: parsed.categoryId || null,
          typeId: parsed.typeId || null,
          temperature: parsed.temperature || null,
          coffeeSortBy: parsed.coffeeSortBy || 'popular',
          coffeeTypeFilter: parsed.coffeeTypeFilter || parsed.typeId || 'type_cappuccino_hot',
          coffeeLocationFilter: parsed.coffeeLocationFilter || 'city',
        };
      }
    } catch (e) {
      console.error('Error loading persisted coffee filter:', e);
    }
    return {
      cafeFilter: 'all',
      typeFilter: 'popular',
      categoryId: null,
      typeId: 'type_cappuccino_hot',
      temperature: null,
      coffeeSortBy: 'popular' as 'cheapest' | 'best-rated' | 'popular',
      coffeeTypeFilter: 'type_cappuccino_hot',
      coffeeLocationFilter: 'city',
    };
  };

  const persistedCoffeeFilter = loadPersistedCoffeeFilter();
  
  // New three-filter system
  const [coffeeSortBy, setCoffeeSortBy] = useState<'cheapest' | 'best-rated' | 'popular'>(persistedCoffeeFilter.coffeeSortBy || 'popular');
  const [coffeeTypeFilter, setCoffeeTypeFilter] = useState<string | null>(persistedCoffeeFilter.coffeeTypeFilter || 'type_cappuccino_hot'); // Default to Cappuccino
  const [coffeeLocationFilter, setCoffeeLocationFilter] = useState<'near-me' | 'city' | 'everywhere'>(persistedCoffeeFilter.coffeeLocationFilter || 'city');
  const [showSortFilter, setShowSortFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  
  // Legacy filter state (keeping for migration/compatibility)
  const [showCoffeeFilter, setShowCoffeeFilter] = useState(false);
  const [cafeFilter, setCafeFilter] = useState<'popular' | 'all'>(persistedCoffeeFilter.cafeFilter);
  const [typeFilter, setTypeFilter] = useState<'popular' | 'categories' | null>(persistedCoffeeFilter.typeFilter);
  const [temperatureFilter, setTemperatureFilter] = useState<'hot' | 'cold' | null>(persistedCoffeeFilter.temperature);
  const [activeCoffeeCategoryId, setActiveCoffeeCategoryId] = useState<string | null>(persistedCoffeeFilter.categoryId);
  const [activeCoffeeTypeId, setActiveCoffeeTypeId] = useState<string | null>(persistedCoffeeFilter.typeId);

  // Get categories filtered by temperature (matches CoffeeMapper2)
  const getCategoriesByTemperature = (temp: 'hot' | 'cold'): Category[] => {
    const hotCategoryIds = [
      'cat_milk_coffee', 'cat_black_coffee', 'cat_filter_coffee', 
      'cat_espresso', 'cat_hot_chocolate', 'cat_chai', 'cat_tea', 
      'cat_matcha', 'cat_kids', 'cat_wellness'
    ];
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];
    
    const categoryIds = temp === 'hot' ? hotCategoryIds : coldCategoryIds;
    return getCategories().filter(cat => categoryIds.includes(cat.id));
  };

  // Get all cold drink types (combines types from both cold categories) - matches CoffeeMapper2
  const getAllColdTypes = (): Type[] => {
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];
    const allColdTypes: Type[] = [];
    
    coldCategoryIds.forEach(categoryId => {
      const types = getTypesForCategory(categoryId);
      allColdTypes.push(...types);
    });
    
    // Sort by priority
    return allColdTypes.sort((a, b) => a.priority - b.priority);
  };

  // Handle temperature selection
  const handleTemperatureSelect = (temp: 'hot' | 'cold') => {
    setTemperatureFilter(temp);
    setActiveCoffeeCategoryId(null);
    setActiveCoffeeTypeId(null);
  };

  // Handle cafe filter change
  const handleCafeFilterChange = (newCafeFilter: 'popular' | 'all') => {
    setCafeFilter(newCafeFilter);
    // Keep type filter when changing cafe filter - don't reset
  };

  // Handle type filter change - reset selections
  const handleTypeFilterChange = (newTypeFilter: 'popular' | 'categories') => {
    setTypeFilter(newTypeFilter);
    setActiveCoffeeCategoryId(null);
    setActiveCoffeeTypeId(null);
    setTemperatureFilter(null);
  };

  // Handle category selection (matches CoffeeMapper2)
  const handleCategorySelect = (categoryId: string) => {
    setActiveCoffeeCategoryId(categoryId);
    setActiveCoffeeTypeId(null); // Reset type when category changes
  };

  // Handle type selection (matches CoffeeMapper2)
  const handleTypeSelect = (typeId: string) => {
    setActiveCoffeeTypeId(typeId);
    setShowCoffeeFilter(false); // Close modal after selection
  };
  
  // Preprocessed coffee locations (use prop if provided, otherwise preprocess locally)
  const [preprocessedCoffeeLocationsLocal, setPreprocessedCoffeeLocationsLocal] = useState<Record<string, any>>({});
  const preprocessedCoffeeLocationsFinal = preprocessedCoffeeLocations || preprocessedCoffeeLocationsLocal;

  // Helper function to get price from a location for a type ID (copied from CoffeeMapper2)
  const getLocationPriceByTypeId = (location: any, typeId: string, size: NormalizedSize = 'regular'): number | null => {
    if (!location || !typeId) return null;
    
    const items: Array<{ name: string; price: number | null }> = [];
    
    // Try coffee_menu format first
    if (location.coffee_menu && typeof location.coffee_menu === 'object') {
      try {
        Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
          if (!menuItem || typeof menuItem !== 'object') return;
          
          if (menuItem._typeId === typeId || menuItem.typeId === typeId) {
            let price: number | null = null;
            
            // Support both new format (Sizes object) and old format (direct size keys)
            const sizesObj = menuItem.Sizes || menuItem;
            if (!sizesObj || typeof sizesObj !== 'object') return;
            
            if (sizesObj[size] !== undefined && typeof sizesObj[size] === 'number') {
              price = sizesObj[size];
            } else {
              const sizeKey = Object.keys(sizesObj).find(k => 
                k.toLowerCase() === size.toLowerCase() && typeof sizesObj[k] === 'number'
              );
              if (sizeKey) {
                price = sizesObj[sizeKey];
              } else if (size === 'regular') {
                const altKeys = ['standard', 'Standard', 'STANDARD', 'medium', 'Medium', 'MEDIUM', 'med', 'Med', 'MED'];
                for (const altKey of altKeys) {
                  if (sizesObj[altKey] !== undefined && typeof sizesObj[altKey] === 'number') {
                    price = sizesObj[altKey];
                    break;
                  }
                }
              }
              if (size === 'small' && price === null && sizesObj['regular'] !== undefined && typeof sizesObj['regular'] === 'number') {
                price = sizesObj['regular'];
              }
              if (price === null) {
                const numericValues = Object.entries(sizesObj)
                  .filter(([key, value]: [string, any]) => 
                    key !== '_typeId' && key !== '_categoryId' && key !== 'typeId' && key !== 'categoryId' &&
                    key !== 'Sizes' &&
                    typeof value === 'number'
                  )
                  .map(([key, value]: [string, any]) => value as number);
                if (numericValues.length > 0) {
                  price = numericValues[0];
                }
              }
            }
            
            if (price !== null) {
              items.push({ name: itemName, price });
            }
          }
        });
      } catch (error) {
        console.error('Error processing coffee_menu:', error);
      }
    }
    
    // Fallback to legacy format
    if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
      location.coffeeHotDrinks.forEach((drink: any) => {
        if (drink.name && drink.typeId === typeId && drink.price) {
          if (drink.price[size] !== undefined && typeof drink.price[size] === 'number') {
            items.push({ name: drink.name, price: drink.price[size] });
          } else if (typeof drink.price === 'number') {
            items.push({ name: drink.name, price: drink.price });
          }
        }
      });
    }
    
    if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
      location.icedCoffeeTea.forEach((drink: any) => {
        if (drink.name && drink.typeId === typeId && drink.price) {
          if (drink.price[size] !== undefined && typeof drink.price[size] === 'number') {
            items.push({ name: drink.name, price: drink.price[size] });
          } else if (typeof drink.price === 'number') {
            items.push({ name: drink.name, price: drink.price });
          }
        }
      });
    }
    
    // Return most common price
    if (items.length > 0) {
      const priceCounts = new Map<number, number>();
      items.forEach(item => {
        if (item.price !== null) {
          const rounded = Math.round(item.price * 100) / 100;
          priceCounts.set(rounded, (priceCounts.get(rounded) || 0) + 1);
        }
      });
      
      let maxCount = 0;
      let mostCommonPrice: number | null = null;
      priceCounts.forEach((count, price) => {
        if (count > maxCount) {
          maxCount = count;
          mostCommonPrice = price;
        }
      });
      
      return mostCommonPrice;
    }
    
    return null;
  };

  // Preprocess coffee locations if not provided as prop
  useEffect(() => {
    if (preprocessedCoffeeLocations && Object.keys(preprocessedCoffeeLocations).length > 0) {
      return; // Already preprocessed
    }
    
    if (Object.keys(allCoffeeLocations).length > 0) {
      const preprocessed: Record<string, any> = {};
      Object.entries(allCoffeeLocations).forEach(([name, location]: [string, any]) => {
        preprocessed[name] = preprocessLocation(location);
      });
      setPreprocessedCoffeeLocationsLocal(preprocessed);
    }
  }, [allCoffeeLocations, preprocessedCoffeeLocations]);

  // Get current location coordinates (from App userLocation when "current location", else selected city)
  const currentLocation = useMemo(() => {
    if (useCurrentLocation) {
      return (userLocation as [number, number] | null) || null;
    }
    return selectedCity
      ? ([selectedCity.latitude, selectedCity.longitude] as [number, number])
      : (userLocation as [number, number] | null);
  }, [selectedCity, userLocation, useCurrentLocation]);

  // Header ref and scroll prevention no longer needed - handled by AppShell/AppHeader

  // Update theme-color for iOS Safari when Home2 is active (matches LocationModal pattern)
  useEffect(() => {
    updateThemeColor('#7c3aed'); // Purple to match Home2 header
    return () => {
      updateThemeColor('#ffffff'); // Restore white when Home2 is unmounted
    };
  }, [selectedCity?.city]); // Use selectedCity.city as dependency similar to LocationModal's details.name

  // Persist location preference to localStorage (coords live in App userLocation)
  useEffect(() => {
    try {
      const locationData = {
        cityName: selectedCity?.city || null,
        useCurrentLocation,
      };
      localStorage.setItem('vibecheck_home2_location', JSON.stringify(locationData));
    } catch (e) {
      console.error('Error persisting location:', e);
    }
  }, [selectedCity, useCurrentLocation]);


  // Sync Filter 3 (coffeeLocationFilter) with header location filter
  useEffect(() => {
    if (useCurrentLocation) {
      // Header is set to "current location", so Filter 3 should be "near-me"
      setCoffeeLocationFilter('near-me');
    } else if (selectedCity) {
      // Header is set to a city, so Filter 3 should be "city"
      setCoffeeLocationFilter('city');
    }
  }, [useCurrentLocation, selectedCity]);

  // Ensure coffee type is selected when "cheapest" is active
  useEffect(() => {
    if (coffeeSortBy === 'cheapest' && !coffeeTypeFilter) {
      // Default to Cappuccino if cheapest is selected but no type is set
      setCoffeeTypeFilter('type_cappuccino_hot');
    }
  }, [coffeeSortBy, coffeeTypeFilter]);

  // Persist coffee filter changes to localStorage
  useEffect(() => {
    try {
      const coffeeFilterData = {
        cafeFilter,
        typeFilter,
        categoryId: activeCoffeeCategoryId,
        typeId: activeCoffeeTypeId,
        temperature: temperatureFilter,
        // New filter system
        coffeeSortBy,
        coffeeTypeFilter,
        coffeeLocationFilter,
      };
      localStorage.setItem('vibecheck_home2_coffee_filter', JSON.stringify(coffeeFilterData));
    } catch (e) {
      console.error('Error persisting coffee filter:', e);
    }
  }, [cafeFilter, typeFilter, activeCoffeeCategoryId, activeCoffeeTypeId, temperatureFilter, coffeeSortBy, coffeeTypeFilter, coffeeLocationFilter]);

  // Cleanup touch timeout on unmount
  useEffect(() => {
    return () => {
      if (showAllScrollTimeoutRef.current) {
        clearTimeout(showAllScrollTimeoutRef.current);
      }
    };
  }, []);

  // Touch handlers for "Show all" buttons to distinguish scrolling from tapping
  const handleShowAllTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    showAllTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    showAllIsScrollingRef.current = false;
    
    if (showAllScrollTimeoutRef.current) {
      clearTimeout(showAllScrollTimeoutRef.current);
    }
  }, []);

  const handleShowAllTouchMove = useCallback(() => {
    showAllIsScrollingRef.current = true;
  }, []);

  const handleShowAllTouchEnd = useCallback((e: React.TouchEvent, handler: () => void) => {
    if (!showAllTouchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - showAllTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - showAllTouchStartRef.current.y);
    const deltaTime = Date.now() - showAllTouchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Thresholds for distinguishing tap from scroll
    const MAX_TAP_DISTANCE = 10; // pixels
    const MAX_TAP_TIME = 300; // milliseconds

    // Only trigger if it was a tap (not a scroll)
    if (
      distance < MAX_TAP_DISTANCE &&
      deltaTime < MAX_TAP_TIME &&
      !showAllIsScrollingRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      handler();
    }

    // Reset after a delay to allow scroll detection
    showAllScrollTimeoutRef.current = setTimeout(() => {
      showAllTouchStartRef.current = null;
      showAllIsScrollingRef.current = false;
    }, 100);
  }, []);

  // Notify parent when location changes (for map centering). Depend on primitives to avoid
  // infinite loop: currentLocation is a new array each render so would retrigger forever.
  const locationLat = currentLocation?.[0];
  const locationLng = currentLocation?.[1];
  useEffect(() => {
    if (onLocationChange && locationLat != null && locationLng != null) {
      onLocationChange([locationLat, locationLng]);
    }
  }, [locationLat, locationLng, onLocationChange]);

  // Get suburb name from current location
  useEffect(() => {
    if (useCurrentLocation && currentLocation) {
      setIsFetchingSuburb(true);
      // Fetch full reverse geocoding response to get suburb property
      fetch(
        `https://api.geoapify.com/v1/geocode/reverse?lat=${currentLocation[0]}&lon=${currentLocation[1]}&apiKey=8dbaf973eca949ca92f61db36c4013cc&format=geojson&lang=en`
      )
        .then((response) => {
          if (!response.ok) throw new Error('Reverse geocoding failed');
          return response.json();
        })
        .then((data) => {
          const feature = data?.features?.[0];
          if (feature?.properties) {
            // Try to get suburb from address components
            // Geoapify provides: suburb, city, state, postcode, etc.
            const suburb =
              feature.properties.suburb ||
                          feature.properties.city_district || 
                          feature.properties.district ||
                          feature.properties.city ||
                          null;
            
            if (suburb) {
              // Clean up suburb name - remove any postcode or state that might be included
              // Suburb should be just the name, e.g., "Bondi" not "Bondi, NSW 2026"
              const cleanSuburb = suburb.split(',')[0].trim();
              setCurrentSuburb(cleanSuburb);
            } else {
              // Fallback: try to extract from formatted address
              const formatted = feature.properties.formatted;
              if (formatted) {
                const parts = formatted.split(',').map((p) => p.trim());
                // Suburb is typically the second part (index 1), or first if no street
                const extractedSuburb = parts.length > 1 ? parts[1] : parts[0];
                // Remove postcode if present (usually 4 digits at the end)
                const cleanSuburb = extractedSuburb.replace(/\s+\d{4}$/, '').trim();
                setCurrentSuburb(cleanSuburb || 'Current Location');
              } else {
                setCurrentSuburb('Current Location');
              }
            }
          } else {
            setCurrentSuburb('Current Location');
          }
        })
        .catch(() => {
          setCurrentSuburb('Current Location');
        })
        .finally(() => {
          setIsFetchingSuburb(false);
        });
    } else {
      setCurrentSuburb('');
    }
  }, [useCurrentLocation, currentLocation]);

  // Request current location from App (triggers browser prompt; App updates userLocation).
  // Keep "Current location" selected on error so the header doesn't flip back to a city.
  const handleUseCurrentLocation = () => {
    if (!onRequestLocation) return;
    setUseCurrentLocation(true);
    setSelectedCity(null);
    onRequestLocation().catch(() => {
      // Don't revert: keep useCurrentLocation true so header stays "Current Location"
    });
  };

  // Fetch shopping centres (10 closest) - similar to how MyVibeModal gets locations with shops
  useEffect(() => {
    // Check if locations are loaded
    const hasLocations = allLocations && Object.keys(allLocations).length > 0;
    if (!hasLocations) {
      setIsLoading(true);
      return;
    }
    
    if (!currentLocation) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);

    const centres: Array<Location & { distance?: number }> = [];
    
    // Get all locations that have shops (shopping centres typically have shops)
    // Similar to how MyVibeModal finds centres
    Object.entries(allLocations).forEach(([key, loc]) => {
      if (!loc || !loc.latitude || !loc.longitude) return;
      
      // Use key as location name if loc.location is not set
      const locationWithName = {
        ...loc,
        location: loc.location || key,
        _id: loc._id || key,
      };
      
      // Check if location has shops
      const shops = locationWithName.shops && typeof locationWithName.shops === 'object' ? locationWithName.shops : {};
      const hasShops = Array.isArray(shops) ? shops.length > 0 : Object.keys(shops || {}).length > 0;
      
      // Also check location name for shopping centre indicators
      const locationName = (locationWithName.location || '').toLowerCase();
      const nameIndicatesShoppingCentre = 
        locationName.includes('westfield') ||
        locationName.includes('shopping centre') ||
        locationName.includes('shopping center') ||
        locationName.includes('mall') ||
        locationName.includes('plaza');
      
      // Include if it has shops OR name indicates it's a shopping centre OR category is Shopping
      if (hasShops || nameIndicatesShoppingCentre || locationWithName.category === 'Shopping') {
        // Always calculate distance when current location is available
        const distance = calculateDistance(
          currentLocation[0],
          currentLocation[1],
          locationWithName.latitude,
          locationWithName.longitude
        );
        centres.push({ ...locationWithName, distance });
      }
    });

    // Sort by distance (always when currentLocation is available)
    centres.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    setShoppingCentres(centres.slice(0, 10));
    setIsLoading(false);
  }, [allLocations, currentLocation]);

  // Fetch beaches
  useEffect(() => {
    if (!allLocations) return;

    const beachLocations: Array<Location & { distance?: number }> = [];
    
    // First try from allLocations
    Object.entries(allLocations).forEach(([key, loc]) => {
      if (loc.category === 'Beaches') {
        // Use key as location name if loc.location is not set
        const locationWithName = {
          ...loc,
          location: loc.location || key,
          _id: loc._id || key,
        };
        
        // Always calculate distance when currentLocation is available
        let distance: number | undefined;
        if (locationWithName.latitude && locationWithName.longitude && currentLocation) {
          distance = calculateDistance(
            currentLocation[0],
            currentLocation[1],
            locationWithName.latitude,
            locationWithName.longitude
          );
        }
        beachLocations.push({ ...locationWithName, distance });
      }
    });

    // Also add from beaches.json if available
    if (beachesData && typeof beachesData === 'object') {
      Object.values(beachesData).forEach((beach: any) => {
        if (beach && typeof beach === 'object' && beach.latitude && beach.longitude) {
          const existingBeach = beachLocations.find(
            (b) => b.location === beach.location || (b.latitude === beach.latitude && b.longitude === beach.longitude)
          );
          
          if (!existingBeach) {
            const distance = currentLocation
              ? calculateDistance(currentLocation[0], currentLocation[1], beach.latitude, beach.longitude)
              : undefined;
            
            beachLocations.push({
              _id: beach._id || beach.location,
              location: beach.location,
              latitude: beach.latitude,
              longitude: beach.longitude,
              category: 'Beaches',
              distance,
              ...beach,
            });
          }
        }
      });
    }

    // Sort by distance when currentLocation is available, otherwise by name
    beachLocations.sort((a, b) => {
      // When using current location, always prioritize distance
      if (useCurrentLocation && currentLocation) {
        // Ensure distance is calculated for all items
        if (a.distance === undefined && currentLocation && a.latitude && a.longitude) {
          a.distance = calculateDistance(currentLocation[0], currentLocation[1], a.latitude, a.longitude);
        }
        if (b.distance === undefined && currentLocation && b.latitude && b.longitude) {
          b.distance = calculateDistance(currentLocation[0], currentLocation[1], b.latitude, b.longitude);
        }
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
      }
      // Otherwise sort by distance if available, then by name
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return (a.location || '').localeCompare(b.location || '');
    });

    setBeaches(beachLocations);
  }, [allLocations, currentLocation, useCurrentLocation]);

  // Fetch coffee locations (with new three-filter system)
  useEffect(() => {
    if (!allCoffeeLocations || Object.keys(allCoffeeLocations).length === 0) return;

    // Determine location reference point based on location filter
    let locationReference: [number, number] | null = null;
    if (coffeeLocationFilter === 'near-me' && currentLocation) {
      locationReference = currentLocation;
    } else if (coffeeLocationFilter === 'city' && selectedCity) {
      locationReference = [selectedCity.latitude, selectedCity.longitude];
    } else if (coffeeLocationFilter === 'city' && !selectedCity && currentLocation) {
      // Fallback to current location if city not selected
      locationReference = currentLocation;
    }
    // 'everywhere' means locationReference stays null

    const coffeeLocs: Array<any & { distance?: number; price?: number | null; rating?: number | null }> = [];
    
    Object.entries(allCoffeeLocations).forEach(([name, location]: [string, any]) => {
      if (!location || !location.latitude || !location.longitude) return;
      
      // Filter by popular cafes if "popular" is selected
      if (coffeeSortBy === 'popular') {
        const isPopular = POPULAR_CAFES.some(popularName => 
          name.toLowerCase().includes(popularName.toLowerCase()) || 
          popularName.toLowerCase().includes(name.toLowerCase()) ||
          (location.location && (
            location.location.toLowerCase().includes(popularName.toLowerCase()) ||
            popularName.toLowerCase().includes(location.location.toLowerCase())
          ))
        );
        if (!isPopular) {
          return; // Skip non-popular cafes
        }
      }
      
      // Filter by coffee type (required when "cheapest" is selected)
      if (coffeeTypeFilter) {
        try {
          const preprocessed = preprocessedCoffeeLocationsFinal[name] || location;
          if (!locationHasType(preprocessed, coffeeTypeFilter)) {
            return; // Skip locations that don't have the selected type
          }
        } catch (error) {
          console.error(`Error checking type for ${name} with type ${coffeeTypeFilter}:`, error);
          return;
        }
      } else if (coffeeSortBy === 'cheapest') {
        // Don't allow cheapest without a coffee type
        return;
      }
      
      const locationWithName = {
        ...location,
        location: location.location || name,
        _id: location._id || name,
        name: name,
      };
      
      // Calculate distance based on location filter
      let distance: number | undefined;
      if (locationReference) {
        distance = calculateDistance(
          locationReference[0],
          locationReference[1],
          locationWithName.latitude,
          locationWithName.longitude
        );
      }
      
      // Get price if type is selected
      let price: number | null = null;
      if (coffeeTypeFilter) {
        try {
          const preprocessed = preprocessedCoffeeLocationsFinal[name] || location;
          price = getLocationPriceByTypeId(preprocessed, coffeeTypeFilter, 'regular');
          
          // Exclude prices < $3
          if (price !== null && price < 3) {
            return; // Skip this location
          }
        } catch (error) {
          console.error(`Error getting price for ${name} with type ${coffeeTypeFilter}:`, error);
        }
      }
      
      // Get rating
      const rating = location.review_rating;
      const ratingValue = rating !== undefined && rating !== null
        ? (typeof rating === 'number' ? rating : parseFloat(String(rating)))
        : null;
      
      coffeeLocs.push({ ...locationWithName, distance, price, rating: ratingValue });
    });

    // Sort based on selected sort method
    if (coffeeSortBy === 'cheapest') {
      // Sort by price (lowest first), then by distance if prices are equal
      coffeeLocs.sort((a, b) => {
        if (a.price === null && b.price === null) {
          return (a.distance || Infinity) - (b.distance || Infinity);
        }
        if (a.price === null) return 1;
        if (b.price === null) return -1;
        if (a.price !== b.price) {
          return a.price - b.price;
        }
        return (a.distance || Infinity) - (b.distance || Infinity);
      });
    } else if (coffeeSortBy === 'popular') {
      // Sort by distance (closest first) for popular cafes
      coffeeLocs.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
    } else {
      // Sort by rating (highest first), then by distance if ratings are equal
      coffeeLocs.sort((a, b) => {
        if (a.rating === null && b.rating === null) {
          return (a.distance || Infinity) - (b.distance || Infinity);
        }
        if (a.rating === null) return 1;
        if (b.rating === null) return -1;
        if (b.rating !== a.rating) {
          return b.rating - a.rating;
        }
        return (a.distance || Infinity) - (b.distance || Infinity);
      });
    }
    
    // Store all filtered locations
    setAllFilteredCoffeeLocations(coffeeLocs);
    // Reset displayed count when filters change
    setDisplayedCoffeeCount(10);
    // Show initial 10 cards
    setCoffeeLocations(coffeeLocs.slice(0, 10));
  }, [allCoffeeLocations, currentLocation, selectedCity, coffeeTypeFilter, coffeeLocationFilter, coffeeSortBy, preprocessedCoffeeLocationsFinal]);

  // Load more cards when displayed count changes
  useEffect(() => {
    if (allFilteredCoffeeLocations.length > 0 && displayedCoffeeCount > 10) {
      setCoffeeLocations(allFilteredCoffeeLocations.slice(0, displayedCoffeeCount));
    }
  }, [displayedCoffeeCount, allFilteredCoffeeLocations]);

  // Handle horizontal scroll to load more cards
  const handleCoffeeCardsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;
    
    // Load more when user scrolls to 80% of the way
    const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;
    if (scrollPercentage > 0.8 && displayedCoffeeCount < allFilteredCoffeeLocations.length) {
      // Load 10 more cards
      setDisplayedCoffeeCount(prev => Math.min(prev + 10, allFilteredCoffeeLocations.length));
    }
  }, [displayedCoffeeCount, allFilteredCoffeeLocations.length]);

  // Get brand locations - matches search functionality
  const brandLocations = useMemo(() => {
    if (!allLocations) return {};

    const brandMap: Record<string, Array<Location & { distance?: number }>> = {};

    BRANDS.forEach((brand) => {
      const locations: Array<Location & { distance?: number }> = [];
      const brandLower = normalizeBrandName(brand).toLowerCase();
      const isLocationOnlyBrand = LOCATION_ONLY_BRANDS.includes(brand as Brand);
      
      Object.entries(allLocations).forEach(([key, loc]) => {
        if (!loc || !loc.latitude || !loc.longitude) return;

        // Use key as location name if loc.location is not set
        const locationWithName = {
          ...loc,
          location: loc.location || key,
          _id: loc._id || key,
        };
        
        const locationNameLower = (locationWithName.location || '').toLowerCase();
        let locationMatches = false;

        // Exclude "adams apple" from Apple brand search
        if (brand === 'Apple' && locationNameLower.includes('adams apple')) {
          return;
        }

        // Check if location name matches brand (e.g., "Westfield Bondi Junction")
        if (locationNameLower.includes(brandLower)) {
          locationMatches = true;
        }
        
        // For non-location-only brands, check shops within locations
        if (!isLocationOnlyBrand && locationWithName.shops) {
          // Handle array format (array of shop name strings)
          if (Array.isArray(locationWithName.shops)) {
            locationWithName.shops.forEach((shop: any) => {
              if (!shop) return;
              // If shop is a string, use it directly
              const shopName =
                typeof shop === 'string'
                ? shop.toLowerCase()
                : (shop.name || shop.storeName || shop.title || shop.shopName || '').toLowerCase();
              
              // Exclude "adams apple" from Apple brand search
              if (brand === 'Apple' && shopName.includes('adams apple')) {
                return;
              }
              
              if (shopName.includes(brandLower) || shopName === brandLower) {
                locationMatches = true;
              }
            });
          } 
          // Handle object format (object with shop keys and shop data)
          else if (typeof locationWithName.shops === 'object') {
            Object.entries(locationWithName.shops).forEach(([shopKey, shop]: [string, any]) => {
              if (!shop) return;
              
              // Get shop name from various possible fields, or use the key
              const shopName =
                typeof shop === 'string'
                ? shop.toLowerCase()
                : (shop.name || shop.storeName || shop.title || shop.shopName || shopKey || '').toLowerCase();
              
              // Exclude "adams apple" from Apple brand search
              if (brand === 'Apple' && shopName.includes('adams apple')) {
                return;
              }
              
              // Match shop name against normalized brand name
              if (shopName.includes(brandLower) || shopName === brandLower) {
                locationMatches = true;
              }
            });
          }
        }

        // If location matches brand, add it to the list
        if (locationMatches) {
          // Check if location already added (avoid duplicates)
          const existingIndex = locations.findIndex((l) => l._id === locationWithName._id);
          if (existingIndex === -1) {
            let distance: number | undefined;
            if (currentLocation) {
              distance = calculateDistance(
                currentLocation[0],
                currentLocation[1],
                locationWithName.latitude,
                locationWithName.longitude
              );
            }
            locations.push({ ...locationWithName, distance });
          }
        }
      });

      // Sort by distance when currentLocation is available, otherwise by name
      locations.sort((a, b) => {
        // When using current location, always prioritize distance
        if (useCurrentLocation && currentLocation) {
          // Ensure distance is calculated for all items
          if (a.distance === undefined && currentLocation && a.latitude && a.longitude) {
            a.distance = calculateDistance(currentLocation[0], currentLocation[1], a.latitude, a.longitude);
          }
          if (b.distance === undefined && currentLocation && b.latitude && b.longitude) {
            b.distance = calculateDistance(currentLocation[0], currentLocation[1], b.latitude, b.longitude);
          }
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          if (a.distance !== undefined) return -1;
          if (b.distance !== undefined) return 1;
        }
        // Otherwise sort by distance if available, then by name
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
        return (a.location || '').localeCompare(b.location || '');
      });
      
      // Always add brand to map, even if no locations found (show 0 locations)
      brandMap[brand] = locations.slice(0, 10);
    });

    return brandMap;
  }, [allLocations, currentLocation, useCurrentLocation]);

  const handleLocationClick = (location: Location) => {
    // Format location for modal
    const formattedLocation: Location = {
      ...location,
      _id: location._id || location.location || '',
      location: location.location || location._id || '',
    };
    onLocationClick(formattedLocation);
  };

  const handleCoffeeLocationClick = (location: any) => {
    if (onCoffeeLocationClick) {
      onCoffeeLocationClick(location);
    } else {
      // Fallback: treat as regular location
      const formattedLocation: Location = {
        ...location,
        _id: location._id || location.name || location.location || '',
        location: location.location || location.name || location._id || '',
      };
      onLocationClick(formattedLocation);
    }
  };

  const handleCitySelect = (city: AustralianCity | null) => {
    if (city === null) {
      // "Current Location" selected
      handleUseCurrentLocation();
      // Sync will happen via useEffect when useCurrentLocation changes
    } else {
      setSelectedCity(city);
      setUseCurrentLocation(false);
      // Sync filter 3 to city
      setCoffeeLocationFilter('city');
    }
    setShowCityFilter(false);
  };

  const renderLocationCard = (location: Location & { distance?: number }, index: number) => {
    const distance = location.distance ? formatDistance(location.distance) : null;
    const locationName = location.location || location._id || 'Unknown Location';
    // Get image URL from shopping centers images
    const imageUrl = getShoppingCenterImageUrl(locationName);
    const hasImage = !!imageUrl;
    
    return (
      <motion.div
        key={location._id || location.location || index}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className="home2-location-card"
        onClick={(e) => {
          e.stopPropagation();
          handleLocationClick(location);
        }}
      >
        <div className="home2-card-image">
          {hasImage ? (
            <img
              src={imageUrl}
              alt={locationName}
              className="home2-card-image-element"
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`home2-card-placeholder ${hasImage ? 'fallback' : ''}`}
            style={{ display: hasImage ? 'none' : 'flex' }}
          >
            <Store size={32} />
          </div>
        </div>
        <div className="home2-card-content">
          <h3 className="home2-card-title">{locationName}</h3>
          {distance && <p className="home2-card-distance">{distance}</p>}
        </div>
      </motion.div>
    );
  };

  const renderBrandCard = (brand: string, locations: Array<Location & { distance?: number }>, index: number) => {
    const BrandIcon = BRAND_ICONS[brand as keyof typeof BRAND_ICONS] || Store;
    
    return (
      <motion.div
        key={brand}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className="home2-brand-card"
        onClick={(e) => {
          e.stopPropagation();
          if (onBrandClick) {
            // Pass the current location (selected city or current location) for ranking
            onBrandClick(brand, locations, currentLocation);
          } else if (locations.length > 0) {
            // Fallback to first location if no brand click handler
            handleLocationClick(locations[0]);
          }
        }}
      >
        <div className="home2-brand-icon">
          <BrandIcon size={32} />
        </div>
        <div className="home2-brand-content">
          <h3 className="home2-brand-title">{brand}</h3>
        </div>
      </motion.div>
    );
  };

  const renderBeachCard = (beach: Location & { distance?: number }, index: number) => {
    const distance = beach.distance ? formatDistance(beach.distance) : null;
    const beachName = beach.location || beach._id || 'Unknown Beach';
    // Get image URL from beaches data
    const beachDataEntry = beachesData[beachName as keyof typeof beachesData];
    const beachImageUrl =
      beachDataEntry && typeof beachDataEntry === 'object' && 'imageUrl' in beachDataEntry
      ? (beachDataEntry as any).imageUrl 
      : (beach as any).imageUrl;
    const hasImage = !!beachImageUrl;
    
    return (
      <motion.div
        key={beach._id || beach.location || index}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className="home2-beach-card"
        onClick={(e) => {
          e.stopPropagation();
          handleLocationClick(beach);
        }}
      >
        <div className="home2-card-image">
          {hasImage ? (
            <img 
              src={beachImageUrl} 
              alt={beachName}
              className="home2-card-image-element"
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`home2-card-placeholder beach ${hasImage ? 'fallback' : ''}`}
            style={{ display: hasImage ? 'none' : 'flex' }}
          >
            <Waves size={32} />
          </div>
        </div>
        <div className="home2-card-content">
          <h3 className="home2-card-title">{beachName}</h3>
          {distance && <p className="home2-card-distance">{distance}</p>}
        </div>
      </motion.div>
    );
  };

  const renderCoffeeCard = (coffeeLocation: any & { distance?: number; price?: number | null; rating?: number | null }, index: number) => {
    const distance = coffeeLocation.distance ? formatDistance(coffeeLocation.distance) : null;
    const coffeeName = coffeeLocation.name || coffeeLocation.location || coffeeLocation._id || 'Unknown Cafe';
    const price = coffeeLocation.price;
    const rating = coffeeLocation.rating;
    
    // Get image URL based on active coffee type filter
    // If no type is selected, use default coffee image
    const defaultCoffeeImage = 'https://images.unsplash.com/photo-1511920170033-f8396924c348?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
    const coffeeImageUrl = coffeeTypeFilter 
      ? COFFEE_TYPE_IMAGES[coffeeTypeFilter] 
      : defaultCoffeeImage;
    const hasImage = !!coffeeImageUrl;
    
    // Build details array for display
    const details: React.ReactNode[] = [];
    if (price !== null && price !== undefined) {
      details.push(
        <span key="price" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#7c3aed' }}>
          ${price.toFixed(2)}
        </span>
      );
    }
    if (rating !== null && !isNaN(rating)) {
      details.push(
        <span key="rating" style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.8)' }}>
          {rating.toFixed(1)}
          <Star size={14} fill="#FFA500" color="#FFA500" style={{ flexShrink: 0 }} />
        </span>
      );
    }
    if (distance) {
      details.push(
        <span key="distance" style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)' }}>
          {distance}
        </span>
      );
    }
    
    return (
      <motion.div
        key={coffeeLocation._id || coffeeLocation.name || index}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        className="home2-location-card"
        onClick={(e) => {
          e.stopPropagation();
          handleCoffeeLocationClick(coffeeLocation);
        }}
      >
        <div className="home2-card-image">
          {hasImage ? (
            <img 
              src={coffeeImageUrl} 
              alt={coffeeName}
              className="home2-card-image-element"
              onError={(e) => {
                // Fallback to icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`home2-card-placeholder ${hasImage ? 'fallback' : ''}`}
            style={{ display: hasImage ? 'none' : 'flex' }}
          >
            <Coffee size={32} />
          </div>
        </div>
        <div className="home2-card-content">
          <h3 className="home2-card-title">{coffeeName}</h3>
          {details.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
              {details.map((detail, i) => (
                <React.Fragment key={i}>
                  {detail}
                  {i < details.length - 1 && (
                    <span style={{ color: 'rgba(17, 24, 39, 0.4)', fontSize: '0.75rem' }}>•</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // Create header configuration for AppHeader
  const headerConfig: HeaderConfig = useMemo(() => ({
    left: (
      <>
        <h1 className="home2-title">VibeCheck</h1>
        <button className="home2-city-filter" onClick={() => setShowCityFilter(true)}>
          <span>
            {useCurrentLocation
              ? !userLocation && isLocating
                ? 'Locating...'
                : currentSuburb || 'Current Location'
              : selectedCity?.city || 'Select Location'}
          </span>
          <ChevronDown size={18} />
        </button>
      </>
    ),
    right: (
      <button 
        className="home2-search-button"
        onTouchStart={(e) => {
          e.stopPropagation();
          if (e.touches.length === 1) {
            // Immediate response on touch start for better mobile UX
            if (onSearchClick) {
              onSearchClick(currentLocation);
            }
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Handle click for desktop (touch events handled by onTouchStart)
          if (onSearchClick) {
            onSearchClick(currentLocation);
          }
        }}
        title="Search"
        aria-label="Search"
      >
        <Search size={18} />
      </button>
    ),
    backgroundColor: '#7c3aed',
    variant: 'default'
  }), [useCurrentLocation, userLocation, isLocating, currentSuburb, selectedCity, onSearchClick, currentLocation]);

  return (
    <AppShell
      headerConfig={headerConfig}
      showFooter={!hideNavbar}
      activeTab={activeTab}
      onTabChange={onTabChange}
      onMyVibeClick={onMyVibeClick}
    >
      <div ref={containerRef} className="home2-page">
        {isLoading ? (
          <div className="home2-loading">
            <div className="home2-spinner"></div>
            <p className="home2-loading-text">Loading locations...</p>
          </div>
        ) : (
          <>
            {/* Coffee Section - Moved to top with new three-filter system */}
            <section className="home2-section">
              <div className="home2-section-header">
                <h2 className="home2-section-title">Coffee</h2>
                <button 
                  type="button"
                  className="home2-section-show-all"
                  onClick={(e) => {
                    // Only handle click if not from a touch event (desktop)
                    if (!showAllTouchStartRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Navigate to coffee map (CoffeeMapper2)
                        onShowAllClick('coffee', [], currentLocation || (selectedCity ? [selectedCity.latitude, selectedCity.longitude] : null));
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchStart(e);
                  }}
                  onTouchMove={(e) => {
                    handleShowAllTouchMove();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchEnd(e, () => {
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Navigate to coffee map (CoffeeMapper2)
                        onShowAllClick('coffee', [], currentLocation || (selectedCity ? [selectedCity.latitude, selectedCity.longitude] : null));
                      }
                    });
                  }}
                >
                  Coffee Map &gt;
                </button>
              </div>
              
              {/* Three Filter Buttons - Mobile Layout */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '0.75rem', 
                marginBottom: '1rem',
              }}>
                {/* First Row: "I want: " + Filter 1 + Filter 3 */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  width: '100%',
                }}>
                  <span style={{
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    I want:
                  </span>
                  
                  {/* Filter 1: Sort By */}
                  <button
                    type="button"
                    onClick={() => setShowSortFilter(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      background: 'rgba(124, 58, 237, 0.1)',
                      border: '1.5px solid rgba(124, 58, 237, 0.3)',
                      borderRadius: '12px',
                      color: '#7c3aed',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      flex: '1 1 auto',
                      minWidth: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(124, 58, 237, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(124, 58, 237, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {coffeeSortBy === 'cheapest' ? <DollarSign size={16} /> : coffeeSortBy === 'popular' ? <Star size={16} fill="#7c3aed" color="#7c3aed" /> : <Star size={16} fill="#7c3aed" color="#7c3aed" />}
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {coffeeSortBy === 'cheapest' ? 'Cheapest' : coffeeSortBy === 'popular' ? 'Popular' : 'Best Rated'}
                    </span>
                    <ChevronDown size={14} style={{ flexShrink: 0 }} />
                  </button>

                  {/* Filter 3: Location */}
                  <button
                    type="button"
                    onClick={() => setShowLocationFilter(true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      background: coffeeLocationFilter !== 'everywhere' ? 'rgba(124, 58, 237, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                      border: coffeeLocationFilter !== 'everywhere' ? '1.5px solid rgba(124, 58, 237, 0.3)' : '1.5px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: '12px',
                      color: coffeeLocationFilter !== 'everywhere' ? '#7c3aed' : '#111827',
                      fontSize: '0.9375rem',
                      fontWeight: coffeeLocationFilter !== 'everywhere' ? 600 : 500,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                      flex: '1 1 auto',
                      minWidth: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (coffeeLocationFilter === 'everywhere') {
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                      } else {
                        e.currentTarget.style.background = 'rgba(124, 58, 237, 0.15)';
                      }
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      if (coffeeLocationFilter === 'everywhere') {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                      } else {
                        e.currentTarget.style.background = 'rgba(124, 58, 237, 0.1)';
                      }
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <MapPin size={16} style={{ flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {coffeeLocationFilter === 'near-me' 
                        ? 'Near Me' 
                        : coffeeLocationFilter === 'city'
                        ? (selectedCity?.city || 'City')
                        : 'Everywhere'}
                    </span>
                    <ChevronDown size={14} style={{ flexShrink: 0 }} />
                  </button>
                </div>

                {/* Second Row: Filter 2 Full Width */}
                <button
                  type="button"
                  onClick={() => setShowTypeFilter(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    background: coffeeTypeFilter ? 'rgba(124, 58, 237, 0.1)' : 'rgba(255, 255, 255, 0.8)',
                    border: coffeeTypeFilter ? '1.5px solid rgba(124, 58, 237, 0.3)' : '1.5px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    color: coffeeTypeFilter ? '#7c3aed' : '#111827',
                    fontSize: '0.9375rem',
                    fontWeight: coffeeTypeFilter ? 600 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                    width: '100%',
                  }}
                  onMouseEnter={(e) => {
                    if (!coffeeTypeFilter) {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                    }
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    if (!coffeeTypeFilter) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                    }
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Coffee size={16} />
                  <span>{coffeeTypeFilter ? getTypeLabel(coffeeTypeFilter) : 'Coffee Type'}</span>
                  <ChevronDown size={14} />
                </button>
              </div>

              {/* Coffee Cards */}
              {coffeeLocations.length > 0 ? (
                <div 
                  ref={coffeeCardsScrollRef}
                  className="home2-cards-scroll"
                  onScroll={handleCoffeeCardsScroll}
                >
                  {coffeeLocations.map((coffee, index) => renderCoffeeCard(coffee, index))}
                </div>
              ) : (
                <div style={{ 
                  padding: '2rem 1rem', 
                  textAlign: 'center', 
                  color: 'rgba(17, 24, 39, 0.6)',
                  fontSize: '0.875rem'
                }}>
                  <Coffee size={40} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
                  <p>No cafes found. Try adjusting your filters.</p>
                </div>
              )}
            </section>

            {/* Shopping Centres Section */}
            <section className="home2-section">
              <div className="home2-section-header">
                <h2 className="home2-section-title">Shopping Centres</h2>
                <button 
                  type="button"
                  className="home2-section-show-all"
                  onClick={(e) => {
                    // Only handle click if not from a touch event (desktop)
                    if (!showAllTouchStartRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Get all shopping centres (not just top 10)
                        const allShoppingCentres: Array<Location & { distance?: number }> = [];
                        Object.entries(allLocations).forEach(([key, loc]) => {
                          if (!loc || !loc.latitude || !loc.longitude) return;
                          const locationWithName = {
                            ...loc,
                            location: loc.location || key,
                            _id: loc._id || key,
                          };
                          const shops =
                            locationWithName.shops && typeof locationWithName.shops === 'object'
                              ? locationWithName.shops
                              : {};
                          const hasShops = Array.isArray(shops) ? shops.length > 0 : Object.keys(shops || {}).length > 0;
                          const locationName = (locationWithName.location || '').toLowerCase();
                          const nameIndicatesShoppingCentre = 
                            locationName.includes('westfield') ||
                            locationName.includes('shopping centre') ||
                            locationName.includes('shopping center') ||
                            locationName.includes('mall') ||
                            locationName.includes('plaza');
                          if (hasShops || nameIndicatesShoppingCentre || locationWithName.category === 'Shopping') {
                            const distance = currentLocation
                              ? calculateDistance(
                              currentLocation[0],
                              currentLocation[1],
                              locationWithName.latitude,
                              locationWithName.longitude
                                )
                              : undefined;
                            allShoppingCentres.push({ ...locationWithName, distance });
                          }
                        });
                        allShoppingCentres.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
                        onShowAllClick('shopping', allShoppingCentres, currentLocation);
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchStart(e);
                  }}
                  onTouchMove={(e) => {
                    handleShowAllTouchMove();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchEnd(e, () => {
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Get all shopping centres (not just top 10)
                        const allShoppingCentres: Array<Location & { distance?: number }> = [];
                        Object.entries(allLocations).forEach(([key, loc]) => {
                          if (!loc || !loc.latitude || !loc.longitude) return;
                          const locationWithName = {
                            ...loc,
                            location: loc.location || key,
                            _id: loc._id || key,
                          };
                          const shops =
                            locationWithName.shops && typeof locationWithName.shops === 'object'
                              ? locationWithName.shops
                              : {};
                          const hasShops = Array.isArray(shops) ? shops.length > 0 : Object.keys(shops || {}).length > 0;
                          const locationName = (locationWithName.location || '').toLowerCase();
                          const nameIndicatesShoppingCentre = 
                            locationName.includes('westfield') ||
                            locationName.includes('shopping centre') ||
                            locationName.includes('shopping center') ||
                            locationName.includes('mall') ||
                            locationName.includes('plaza');
                          if (hasShops || nameIndicatesShoppingCentre || locationWithName.category === 'Shopping') {
                            const distance = currentLocation
                              ? calculateDistance(
                              currentLocation[0],
                              currentLocation[1],
                              locationWithName.latitude,
                              locationWithName.longitude
                                )
                              : undefined;
                            allShoppingCentres.push({ ...locationWithName, distance });
                          }
                        });
                        allShoppingCentres.sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));
                        onShowAllClick('shopping', allShoppingCentres, currentLocation);
                      }
                    });
                  }}
                >
                  Show all &gt;
                </button>
          </div>
          <div className="home2-cards-scroll">
                {shoppingCentres.map((centre, index) => renderLocationCard(centre, index))}
          </div>
        </section>

            {/* Beaches Section */}
            <section className="home2-section">
              <div className="home2-section-header">
                <h2 className="home2-section-title">Beaches</h2>
                <button 
                  type="button"
                  className="home2-section-show-all"
                  onClick={(e) => {
                    // Only handle click if not from a touch event (desktop)
                    if (!showAllTouchStartRef.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Get all beaches - only locations with category === 'Beaches'
                        const allBeaches: Array<Location & { distance?: number }> = [];
                        
                        // First, get beaches from allLocations (only those with category === 'Beaches')
                        Object.entries(allLocations).forEach(([key, loc]) => {
                          // Only include locations that are explicitly categorized as Beaches
                          if (loc && loc.category === 'Beaches') {
                            const locationWithName = {
                              ...loc,
                              location: loc.location || key,
                              _id: loc._id || key,
                            };
                            
                            // Only add if it has valid coordinates
                            if (locationWithName.latitude && locationWithName.longitude) {
                              let distance: number | undefined;
                              if (currentLocation) {
                                distance = calculateDistance(
                                  currentLocation[0],
                                  currentLocation[1],
                                  locationWithName.latitude,
                                  locationWithName.longitude
                                );
                              }
                              allBeaches.push({ ...locationWithName, distance });
                            }
                          }
                        });
                        
                        // Also add from beaches.json (these are all beaches by definition)
                        if (beachesData && typeof beachesData === 'object') {
                          Object.values(beachesData).forEach((beach: any) => {
                            // Only add if it has valid coordinates
                            if (beach && typeof beach === 'object' && beach.latitude && beach.longitude) {
                              const existingBeach = allBeaches.find(
                                (b) =>
                                b.location === beach.location || 
                                (b.latitude === beach.latitude && b.longitude === beach.longitude)
                              );
                              
                              // Only add if it doesn't already exist
                              if (!existingBeach) {
                                const distance = currentLocation
                                  ? calculateDistance(
                                  currentLocation[0],
                                  currentLocation[1],
                                  beach.latitude,
                                  beach.longitude
                                    )
                                  : undefined;
                                
                                allBeaches.push({
                                  _id: beach._id || beach.location,
                                  location: beach.location,
                                  latitude: beach.latitude,
                                  longitude: beach.longitude,
                                  category: 'Beaches', // Explicitly set category
                                  distance,
                                  ...beach,
                                });
                              }
                            }
                          });
                        }

                        // Sort by distance when currentLocation is available, otherwise by name
                        allBeaches.sort((a, b) => {
                          // When using current location, always prioritize distance
                          if (currentLocation) {
                            // Ensure distance is calculated for all items
                            if (a.distance === undefined && a.latitude && a.longitude) {
                              a.distance = calculateDistance(
                                currentLocation[0],
                                currentLocation[1],
                                a.latitude,
                                a.longitude
                              );
                            }
                            if (b.distance === undefined && b.latitude && b.longitude) {
                              b.distance = calculateDistance(
                                currentLocation[0],
                                currentLocation[1],
                                b.latitude,
                                b.longitude
                              );
                            }
                            if (a.distance !== undefined && b.distance !== undefined) {
                              return a.distance - b.distance;
                            }
                            if (a.distance !== undefined) return -1;
                            if (b.distance !== undefined) return 1;
                          }
                          // Otherwise sort by name
                          return (a.location || '').localeCompare(b.location || '');
                        });

                        onShowAllClick('beaches', allBeaches, currentLocation);
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchStart(e);
                  }}
                  onTouchMove={(e) => {
                    handleShowAllTouchMove();
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleShowAllTouchEnd(e, () => {
                      // Simple debounce to prevent rapid double-taps
                      const now = Date.now();
                      if (now - lastClickTimeRef.current < 500) {
                        return;
                      }
                      lastClickTimeRef.current = now;
                      
                      if (onShowAllClick) {
                        // Get all beaches - only locations with category === 'Beaches'
                        const allBeaches: Array<Location & { distance?: number }> = [];
                        
                        // First, get beaches from allLocations (only those with category === 'Beaches')
                        Object.entries(allLocations).forEach(([key, loc]) => {
                          // Only include locations that are explicitly categorized as Beaches
                          if (loc && loc.category === 'Beaches') {
                            const locationWithName = {
                              ...loc,
                              location: loc.location || key,
                              _id: loc._id || key,
                            };
                            
                            // Only add if it has valid coordinates
                            if (locationWithName.latitude && locationWithName.longitude) {
                              let distance: number | undefined;
                              if (currentLocation) {
                                distance = calculateDistance(
                                  currentLocation[0],
                                  currentLocation[1],
                                  locationWithName.latitude,
                                  locationWithName.longitude
                                );
                              }
                              allBeaches.push({ ...locationWithName, distance });
                            }
                          }
                        });
                        
                        // Also add from beaches.json (these are all beaches by definition)
                        if (beachesData && typeof beachesData === 'object') {
                          Object.values(beachesData).forEach((beach: any) => {
                            // Only add if it has valid coordinates
                            if (beach && typeof beach === 'object' && beach.latitude && beach.longitude) {
                              const existingBeach = allBeaches.find(
                                (b) =>
                                b.location === beach.location || 
                                (b.latitude === beach.latitude && b.longitude === beach.longitude)
                              );
                              
                              // Only add if it doesn't already exist
                              if (!existingBeach) {
                                const distance = currentLocation
                                  ? calculateDistance(
                                  currentLocation[0],
                                  currentLocation[1],
                                  beach.latitude,
                                  beach.longitude
                                    )
                                  : undefined;
                                
                                allBeaches.push({
                                  _id: beach._id || beach.location,
                                  location: beach.location,
                                  latitude: beach.latitude,
                                  longitude: beach.longitude,
                                  category: 'Beaches', // Explicitly set category
                                  distance,
                                  ...beach,
                                });
                              }
                            }
                          });
                        }

                        // Sort by distance when currentLocation is available, otherwise by name
                        allBeaches.sort((a, b) => {
                          // When using current location, always prioritize distance
                          if (currentLocation) {
                            // Ensure distance is calculated for all items
                            if (a.distance === undefined && a.latitude && a.longitude) {
                              a.distance = calculateDistance(
                                currentLocation[0],
                                currentLocation[1],
                                a.latitude,
                                a.longitude
                              );
                            }
                            if (b.distance === undefined && b.latitude && b.longitude) {
                              b.distance = calculateDistance(
                                currentLocation[0],
                                currentLocation[1],
                                b.latitude,
                                b.longitude
                              );
                            }
                            if (a.distance !== undefined && b.distance !== undefined) {
                              return a.distance - b.distance;
                            }
                            if (a.distance !== undefined) return -1;
                            if (b.distance !== undefined) return 1;
                          }
                          // Otherwise sort by name
                          return (a.location || '').localeCompare(b.location || '');
                        });

                        onShowAllClick('beaches', allBeaches, currentLocation);
                      }
                    });
                  }}
                >
                  Show all &gt;
                </button>
              </div>
              <div className="home2-cards-scroll">{beaches.map((beach, index) => renderBeachCard(beach, index))}</div>
            </section>

            {/* Brands Section */}
            <section className="home2-section">
              <div className="home2-section-header">
                <h2 className="home2-section-title">Brands</h2>
                <span
                  className="home2-section-show-all"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onShowAllBrandsClick) {
                      onShowAllBrandsClick(currentLocation);
                    }
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onShowAllBrandsClick) {
                      onShowAllBrandsClick(currentLocation);
                    }
                  }}
                >
                  Show all &gt;
                </span>
              </div>
              <div className="home2-cards-scroll">
                {Object.entries(brandLocations).map(([brand, locations], index) =>
                  renderBrandCard(brand, locations, index)
                )}
              </div>
            </section>
          </>
        )}

        {/* City Filter Modal - Portal to document.body for proper z-index */}
        {createPortal(
          <AnimatePresence>
          {showCityFilter && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="home2-city-filter-overlay"
              onClick={() => setShowCityFilter(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="home2-city-filter-modal"
                onClick={(e) => e.stopPropagation()}
              >
              <div className="home2-city-filter-header">
                <h2>Select Your Location</h2>
                <button onClick={() => setShowCityFilter(false)}>✕</button>
              </div>
              <div className="home2-city-filter-content">
                {/* Current Location Option */}
                <div className="home2-city-filter-state">
                  <button
                    className={`home2-city-option ${useCurrentLocation ? 'selected' : ''}`}
                    onClick={() => handleCitySelect(null)}
                  >
                    <Navigation size={18} />
                    <span>Current Location</span>
                    {useCurrentLocation && <span className="checkmark">✓</span>}
                  </button>
                </div>
                
                {/* Cities by State */}
                {Object.entries(
                  AUSTRALIAN_CITIES.reduce((acc, city) => {
                    if (!acc[city.state]) acc[city.state] = [];
                    acc[city.state].push(city);
                    return acc;
                  }, {} as Record<string, AustralianCity[]>)
                ).map(([state, cities]) => (
                  <div key={state} className="home2-city-filter-state">
                    <h3>{state}</h3>
                    {cities.map((city) => (
                      <button
                        key={city.city}
                        className={`home2-city-option ${selectedCity?.city === city.city ? 'selected' : ''}`}
                        onClick={() => handleCitySelect(city)}
                      >
                        <span>{city.city}</span>
                        {selectedCity?.city === city.city && <span className="checkmark">✓</span>}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>,
          document.body
        )}

        {/* Coffee Filter Modal - Portal to document.body for proper z-index */}
        {createPortal(
          <AnimatePresence>
          {showCoffeeFilter && (
            <motion.div
              key="coffee-filter-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="home2-city-filter-overlay"
              onClick={() => setShowCoffeeFilter(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="home2-city-filter-modal"
                onClick={(e) => e.stopPropagation()}
              >
              <div className="home2-city-filter-header">
                <h3>Coffee Type</h3>
                {(typeFilter !== null || activeCoffeeTypeId) ? (
                  <button 
                    onClick={() => {
                      setCafeFilter('all');
                      setTypeFilter(null);
                      setActiveCoffeeTypeId(null);
                      setActiveCoffeeCategoryId(null);
                      setTemperatureFilter(null);
                    }}
                    style={{ fontSize: '0.875rem', color: '#7c3aed', fontWeight: 500 }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <div className="home2-city-filter-content">
                {/* Level 1: Cafe Filter - Popular Cafes / All Cafes */}
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'rgba(17, 24, 39, 0.6)',
                    marginBottom: '8px',
                    textAlign: 'center',
                    fontWeight: 500,
                  }}
                >
                  Choose cafes
                </div>
                <div
                  className="coffee-filter-mode-switcher"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '12px',
                    marginBottom: '20px',
                    width: '100%',
                    position: 'relative',
                    zIndex: 1000,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleCafeFilterChange('popular')}
                    className={`coffee-filter-mode-button ${cafeFilter === 'popular' ? 'active' : ''}`}
                    style={{
                      padding: '14px 20px',
                      background: cafeFilter === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                      border: `1.5px solid ${cafeFilter === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                      borderRadius: '12px',
                      color: cafeFilter === 'popular' ? '#7c3aed' : '#111827',
                      fontSize: '0.9375rem',
                      fontWeight: cafeFilter === 'popular' ? 600 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>Popular Cafes</span>
                    {cafeFilter === 'popular' && <Star size={16} style={{ color: '#7c3aed' }} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCafeFilterChange('all')}
                    className={`coffee-filter-mode-button ${cafeFilter === 'all' ? 'active' : ''}`}
                    style={{
                      padding: '14px 20px',
                      background: cafeFilter === 'all' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                      border: `1.5px solid ${cafeFilter === 'all' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                      borderRadius: '12px',
                      color: cafeFilter === 'all' ? '#7c3aed' : '#111827',
                      fontSize: '0.9375rem',
                      fontWeight: cafeFilter === 'all' ? 600 : 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <span>All Cafes Nearby</span>
                  </button>
                </div>

                {/* Level 2: Type Filter - Popular Types / Categories (only show after cafe filter is selected) */}
                {cafeFilter && (
                  <>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '8px',
                        textAlign: 'center',
                        fontWeight: 500,
                      }}
                    >
                      Choose filter method
                    </div>
                    <div
                      className="coffee-filter-mode-switcher"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        marginBottom: '12px',
                        width: '100%',
                        position: 'relative',
                        zIndex: 1000,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleTypeFilterChange('popular')}
                        className={`coffee-filter-mode-button ${typeFilter === 'popular' ? 'active' : ''}`}
                        style={{
                          padding: '14px 20px',
                          background: typeFilter === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${typeFilter === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: typeFilter === 'popular' ? '#7c3aed' : '#111827',
                          fontSize: '0.9375rem',
                          fontWeight: typeFilter === 'popular' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <span>Popular Types</span>
                        {typeFilter === 'popular' && <Star size={16} style={{ color: '#7c3aed' }} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleTypeFilterChange('categories')}
                        className={`coffee-filter-mode-button ${typeFilter === 'categories' ? 'active' : ''}`}
                        style={{
                          padding: '14px 20px',
                          background: typeFilter === 'categories' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${typeFilter === 'categories' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: typeFilter === 'categories' ? '#7c3aed' : '#111827',
                          fontSize: '0.9375rem',
                          fontWeight: typeFilter === 'categories' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <span>Categories</span>
                        {typeFilter === 'categories' && <List size={16} style={{ color: '#7c3aed' }} />}
                      </button>
                    </div>
                  </>
                )}

                {/* Popular Types Mode - Show only popular types (only show after type filter is selected) */}
                {typeFilter === 'popular' && (
                  <div className="coffee-filter-types">
                    <div
                      style={{
                        fontSize: '0.8125rem',
                        color: 'rgba(17, 24, 39, 0.5)',
                        marginBottom: '10px',
                        textAlign: 'center',
                        fontWeight: 400,
                      }}
                    >
                      Choose a coffee type
                    </div>
                    <div className="coffee-filter-grid">
                      {getPopularTypeIds()
                        .filter(typeId => typeId !== 'type_turmeric_latte')
                        .map((typeId) => (
                        <button
                          key={typeId}
                          onClick={() => handleTypeSelect(typeId)}
                          className={`coffee-filter-card ${activeCoffeeTypeId === typeId ? 'selected' : ''}`}
                        >
                          <span className="coffee-filter-card-name">{getTypeLabel(typeId)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories Mode - Temperature Selection (matches CoffeeMapper2) */}
                {typeFilter === 'categories' && temperatureFilter === null && (
                  <div className="coffee-filter-temperature">
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '16px',
                        textAlign: 'center',
                        fontWeight: 500,
                      }}
                    >
                      Select a drink type to continue
                    </div>
                    <div className="coffee-filter-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <button
                        onClick={() => handleTemperatureSelect('hot')}
                        className="coffee-filter-card coffee-filter-temperature-card"
                        style={{
                          padding: '24px 20px',
                          fontSize: '1rem',
                          fontWeight: 600,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span className="coffee-filter-card-name">Coffee / Hot Drinks</span>
                        <ChevronDown 
                          size={20} 
                          style={{ 
                            color: 'rgba(17, 24, 39, 0.4)',
                            transform: 'rotate(-90deg)',
                            marginTop: '4px'
                          }} 
                        />
                      </button>
                      <button
                        onClick={() => handleTemperatureSelect('cold')}
                        className="coffee-filter-card coffee-filter-temperature-card"
                        style={{
                          padding: '24px 20px',
                          fontSize: '1rem',
                          fontWeight: 600,
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span className="coffee-filter-card-name">Cold / Iced Drinks</span>
                        <ChevronDown 
                          size={20} 
                          style={{ 
                            color: 'rgba(17, 24, 39, 0.4)',
                            transform: 'rotate(-90deg)',
                            marginTop: '4px'
                          }} 
                        />
                      </button>
                    </div>
                  </div>
                )}

                {/* Categories for Hot Drinks (matches CoffeeMapper2) */}
                {typeFilter === 'categories' && temperatureFilter === 'hot' && !activeCoffeeCategoryId && (
                  <div className="coffee-filter-categories">
                    <button
                      onClick={() => setTemperatureFilter(null)}
                      className="coffee-filter-back-button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color: '#111827',
                        marginBottom: '12px',
                      }}
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Coffee / Hot Drinks
                    </div>
                    <div className="coffee-filter-grid">
                      {getCategoriesByTemperature('hot').map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleCategorySelect(category.id)}
                          className="coffee-filter-card"
                        >
                          <span className="coffee-filter-card-name">{category.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cold Drink Types (directly, skip categories - matches CoffeeMapper2) */}
                {typeFilter === 'categories' && temperatureFilter === 'cold' && !activeCoffeeCategoryId && (
                  <div className="coffee-filter-types">
                    <button
                      onClick={() => setTemperatureFilter(null)}
                      className="coffee-filter-back-button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color: '#111827',
                        marginBottom: '12px',
                      }}
                    >
                      <ArrowLeft size={18} />
                      Back
                    </button>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Cold / Iced Drinks
                      {!activeCoffeeTypeId && (
                        <span
                          style={{
                            fontSize: '0.8125rem',
                            color: 'rgba(17, 24, 39, 0.5)',
                            textTransform: 'none',
                            letterSpacing: 'normal',
                            fontWeight: 400,
                            marginLeft: '8px',
                          }}
                        >
                          • Choose a coffee type
                        </span>
                      )}
                    </div>
                    <div className="coffee-filter-grid">
                      {getAllColdTypes().map((type) => (
                        <button
                          key={type.id}
                          onClick={() => handleTypeSelect(type.id)}
                          className={`coffee-filter-card ${activeCoffeeTypeId === type.id ? 'selected' : ''}`}
                        >
                          <span className="coffee-filter-card-name">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Types for Selected Hot Category (matches CoffeeMapper2) */}
                {typeFilter === 'categories' && temperatureFilter === 'hot' && activeCoffeeCategoryId && (
                  <>
                    <button
                      onClick={() => setActiveCoffeeCategoryId(null)}
                      className="coffee-filter-back-button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        color: '#111827',
                        marginBottom: '12px',
                      }}
                    >
                      <ArrowLeft size={18} />
                      Back to Categories
                    </button>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(17, 24, 39, 0.6)',
                        marginBottom: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {getCategoryLabel(activeCoffeeCategoryId)}
                    </div>
                    <div className="coffee-filter-grid">
                      {getTypesForCategory(activeCoffeeCategoryId).map((type) => (
                        <button
                          key={type.id}
                          onClick={() => handleTypeSelect(type.id)}
                          className={`coffee-filter-card ${activeCoffeeTypeId === type.id ? 'selected' : ''}`}
                        >
                          <span className="coffee-filter-card-name">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              </motion.div>
            </motion.div>
          )}
          </AnimatePresence>,
          document.body
        )}

        {/* Sort Filter Modal - Portal to document.body */}
        {createPortal(
          <AnimatePresence>
            {showSortFilter && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={() => setShowSortFilter(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <h2>Sort By</h2>
                    <button onClick={() => setShowSortFilter(false)} className="coffee-filter-clear-button">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="coffee-filter-modal-content">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <button
                        onClick={() => {
                          setCoffeeSortBy('cheapest');
                          setShowSortFilter(false);
                        }}
                        className={`coffee-filter-mode-button ${coffeeSortBy === 'cheapest' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeSortBy === 'cheapest' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeSortBy === 'cheapest' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeSortBy === 'cheapest' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeSortBy === 'cheapest' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <DollarSign size={20} />
                        <span>Cheapest</span>
                        {coffeeSortBy === 'cheapest' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                      <button
                        onClick={() => {
                          setCoffeeSortBy('best-rated');
                          setShowSortFilter(false);
                        }}
                        className={`coffee-filter-mode-button ${coffeeSortBy === 'best-rated' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeSortBy === 'best-rated' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeSortBy === 'best-rated' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeSortBy === 'best-rated' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeSortBy === 'best-rated' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <Star size={20} fill={coffeeSortBy === 'best-rated' ? '#7c3aed' : 'none'} color={coffeeSortBy === 'best-rated' ? '#7c3aed' : '#111827'} />
                        <span>Best Rated</span>
                        {coffeeSortBy === 'best-rated' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                      <button
                        onClick={() => {
                          setCoffeeSortBy('popular');
                          setShowSortFilter(false);
                        }}
                        className={`coffee-filter-mode-button ${coffeeSortBy === 'popular' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeSortBy === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeSortBy === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeSortBy === 'popular' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeSortBy === 'popular' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <Star size={20} fill={coffeeSortBy === 'popular' ? '#7c3aed' : 'none'} color={coffeeSortBy === 'popular' ? '#7c3aed' : '#111827'} />
                        <span>Popular</span>
                        {coffeeSortBy === 'popular' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Coffee Type Filter Modal - Portal to document.body */}
        {createPortal(
          <AnimatePresence>
            {showTypeFilter && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={() => setShowTypeFilter(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <h2>Coffee Type</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {coffeeSortBy === 'cheapest' && (
                        <span style={{ fontSize: '0.75rem', color: 'rgba(17, 24, 39, 0.6)', marginRight: '8px' }}>
                          Required for cheapest
                        </span>
                      )}
                      <button onClick={() => setShowTypeFilter(false)} className="coffee-filter-clear-button">
                        <X size={20} />
                      </button>
                    </div>
                  </div>
                  <div className="coffee-filter-modal-content">
                    {/* Filter Methods - Same Row */}
                    <div style={{ marginBottom: '16px' }}>
                      <div className="coffee-filter-mode-switcher" style={{ 
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                      }}>
                        <button
                          type="button"
                          onClick={() => {
                            setTypeFilter('popular');
                            setActiveCoffeeCategoryId(null);
                            setTemperatureFilter(null);
                          }}
                          className={`coffee-filter-mode-button ${typeFilter === 'popular' ? 'active' : ''}`}
                          style={{
                            padding: '14px 20px',
                            background: typeFilter === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                            border: `1.5px solid ${typeFilter === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                            borderRadius: '12px',
                            color: typeFilter === 'popular' ? '#7c3aed' : '#111827',
                            fontSize: '0.9375rem',
                            fontWeight: typeFilter === 'popular' ? 600 : 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>Popular</span>
                          {typeFilter === 'popular' && <Star size={16} style={{ color: '#7c3aed' }} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTypeFilter('categories');
                            setActiveCoffeeCategoryId(null);
                            setActiveCoffeeTypeId(null);
                            setTemperatureFilter(null);
                          }}
                          className={`coffee-filter-mode-button ${typeFilter === 'categories' ? 'active' : ''}`}
                          style={{
                            padding: '14px 20px',
                            background: typeFilter === 'categories' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                            border: `1.5px solid ${typeFilter === 'categories' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                            borderRadius: '12px',
                            color: typeFilter === 'categories' ? '#7c3aed' : '#111827',
                            fontSize: '0.9375rem',
                            fontWeight: typeFilter === 'categories' ? 600 : 500,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          <span>Categories</span>
                          {typeFilter === 'categories' && <List size={16} style={{ color: '#7c3aed' }} />}
                        </button>
                      </div>
                    </div>

                    {/* Popular Types */}
                    {typeFilter === 'popular' && (
                      <div className="coffee-filter-types">
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textAlign: 'center' }}>
                          Choose a drink type
                        </div>
                        <div className="coffee-filter-grid">
                          {getPopularTypeIds().map((typeId) => (
                            <button
                              key={typeId}
                              onClick={() => {
                                setCoffeeTypeFilter(typeId);
                                setShowTypeFilter(false);
                              }}
                              className={`coffee-filter-card ${coffeeTypeFilter === typeId ? 'selected' : ''}`}
                            >
                              <span className="coffee-filter-card-name">{getTypeLabel(typeId)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Categories Mode - Temperature Selection */}
                    {typeFilter === 'categories' && temperatureFilter === null && (
                      <div className="coffee-filter-temperature">
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '16px', textAlign: 'center' }}>
                          Select a drink type to continue
                        </div>
                        <div className="coffee-filter-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <button
                            onClick={() => handleTemperatureSelect('hot')}
                            className="coffee-filter-card coffee-filter-temperature-card"
                            style={{
                              padding: '24px 20px',
                              fontSize: '1rem',
                              fontWeight: 600,
                            }}
                          >
                            <span className="coffee-filter-card-name">Coffee / Hot Drinks</span>
                            <ChevronDown
                              size={20}
                              style={{
                                color: 'rgba(17, 24, 39, 0.4)',
                                transform: 'rotate(-90deg)',
                                marginTop: '4px',
                              }}
                            />
                          </button>
                          <button
                            onClick={() => handleTemperatureSelect('cold')}
                            className="coffee-filter-card coffee-filter-temperature-card"
                            style={{
                              padding: '24px 20px',
                              fontSize: '1rem',
                              fontWeight: 600,
                            }}
                          >
                            <span className="coffee-filter-card-name">Cold / Iced Drinks</span>
                            <ChevronDown
                              size={20}
                              style={{
                                color: 'rgba(17, 24, 39, 0.4)',
                                transform: 'rotate(-90deg)',
                                marginTop: '4px',
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Hot Categories */}
                    {typeFilter === 'categories' && temperatureFilter === 'hot' && !activeCoffeeCategoryId && (
                      <div className="coffee-filter-categories">
                        <button
                          onClick={() => setTemperatureFilter(null)}
                          className="coffee-filter-back-button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            background: 'rgba(0, 0, 0, 0.05)',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            color: '#111827',
                            marginBottom: '12px',
                          }}
                        >
                          <ArrowLeft size={18} />
                          Back
                        </button>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textAlign: 'center' }}>
                          Choose a drink type
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Coffee / Hot Drinks
                        </div>
                        <div className="coffee-filter-grid">
                          {getCategoriesByTemperature('hot').map((category) => (
                            <button
                              key={category.id}
                              onClick={() => handleCategorySelect(category.id)}
                              className="coffee-filter-card"
                            >
                              <span className="coffee-filter-card-name">{category.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cold Types */}
                    {typeFilter === 'categories' && temperatureFilter === 'cold' && !activeCoffeeCategoryId && (
                      <div className="coffee-filter-types">
                        <button
                          onClick={() => setTemperatureFilter(null)}
                          className="coffee-filter-back-button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            background: 'rgba(0, 0, 0, 0.05)',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            color: '#111827',
                            marginBottom: '12px',
                          }}
                        >
                          <ArrowLeft size={18} />
                          Back
                        </button>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textAlign: 'center' }}>
                          Choose a drink type
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Cold / Iced Drinks
                        </div>
                        <div className="coffee-filter-grid">
                          {getAllColdTypes().map((type) => (
                            <button
                              key={type.id}
                              onClick={() => {
                                setCoffeeTypeFilter(type.id);
                                setShowTypeFilter(false);
                              }}
                              className={`coffee-filter-card ${coffeeTypeFilter === type.id ? 'selected' : ''}`}
                            >
                              <span className="coffee-filter-card-name">{type.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hot Category Types */}
                    {typeFilter === 'categories' && temperatureFilter === 'hot' && activeCoffeeCategoryId && (
                      <div className="coffee-filter-types">
                        <button
                          onClick={() => setActiveCoffeeCategoryId(null)}
                          className="coffee-filter-back-button"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            background: 'rgba(0, 0, 0, 0.05)',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            color: '#111827',
                            marginBottom: '12px',
                          }}
                        >
                          <ArrowLeft size={18} />
                          Back
                        </button>
                        <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.6)', marginBottom: '12px', textAlign: 'center' }}>
                          Choose a drink type
                        </div>
                        <div className="coffee-filter-grid">
                          {getTypesForCategory(activeCoffeeCategoryId).map((type) => (
                            <button
                              key={type.id}
                              onClick={() => {
                                setCoffeeTypeFilter(type.id);
                                setShowTypeFilter(false);
                              }}
                              className={`coffee-filter-card ${coffeeTypeFilter === type.id ? 'selected' : ''}`}
                            >
                              <span className="coffee-filter-card-name">{type.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Location Filter Modal - Portal to document.body */}
        {createPortal(
          <AnimatePresence>
            {showLocationFilter && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={() => setShowLocationFilter(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <h2>Location</h2>
                    <button onClick={() => setShowLocationFilter(false)} className="coffee-filter-clear-button">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="coffee-filter-modal-content">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <button
                        onClick={() => {
                          // Sync with header location - set to use current location
                          if (currentLocation || userLocation) {
                            setUseCurrentLocation(true);
                            setSelectedCity(null);
                            setCoffeeLocationFilter('near-me');
                            if (!currentLocation && userLocation) {
                              handleUseCurrentLocation();
                            }
                          } else {
                            // Current location not available, just set the filter
                            setCoffeeLocationFilter('near-me');
                          }
                          setShowLocationFilter(false);
                        }}
                        className={`coffee-filter-mode-button ${coffeeLocationFilter === 'near-me' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeLocationFilter === 'near-me' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeLocationFilter === 'near-me' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeLocationFilter === 'near-me' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeLocationFilter === 'near-me' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <Navigation size={20} />
                        <span>Near Me</span>
                        {coffeeLocationFilter === 'near-me' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                      <button
                        onClick={() => {
                          // Sync with header location - use selected city
                          setUseCurrentLocation(false);
                          if (!selectedCity) {
                            // If no city selected, open city selector
                            setShowLocationFilter(false);
                            setShowCityFilter(true);
                          } else {
                            setCoffeeLocationFilter('city');
                            setShowLocationFilter(false);
                          }
                        }}
                        className={`coffee-filter-mode-button ${coffeeLocationFilter === 'city' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeLocationFilter === 'city' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeLocationFilter === 'city' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeLocationFilter === 'city' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeLocationFilter === 'city' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <MapPin size={20} />
                        <span>{selectedCity?.city || 'City'}</span>
                        {coffeeLocationFilter === 'city' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                      <button
                        onClick={() => {
                          setCoffeeLocationFilter('everywhere');
                          setShowLocationFilter(false);
                        }}
                        className={`coffee-filter-mode-button ${coffeeLocationFilter === 'everywhere' ? 'active' : ''}`}
                        style={{
                          padding: '16px 20px',
                          background: coffeeLocationFilter === 'everywhere' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                          border: `1.5px solid ${coffeeLocationFilter === 'everywhere' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                          borderRadius: '12px',
                          color: coffeeLocationFilter === 'everywhere' ? '#7c3aed' : '#111827',
                          fontSize: '1rem',
                          fontWeight: coffeeLocationFilter === 'everywhere' ? 600 : 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <span>Everywhere</span>
                        {coffeeLocationFilter === 'everywhere' && <span style={{ marginLeft: 'auto' }}>✓</span>}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </AppShell>
  );
};

export default Home2;
